import { message } from 'antd'
import React, { useEffect, useState } from 'react'
import { initCallpayPayment } from '../utils/utils'
import { handleError } from '../../lib/utils'
import MobMedicalHistoryLoadingOverlay from '../../Patient/Medical History/Mobile/components/LoadingOverlay'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCreditCard } from '@fortawesome/free-solid-svg-icons'
import { findCallpayOriginalTransaction } from '../utils/utils'
import { findCallpayTransaction } from '../utils/utils'

const EmbeddedPayment = ({ patientRecord, amount, paymentType, isAuthorization, callback }) => {
  const [loading, setLoading] = useState(true)
  const [paymentResponse, setPaymentResponse] = useState(null)
  const [iframeLoading, setIframeLoading] = useState(true)

  useEffect(() => {
    handleInitCheckout()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    let intervalId = null
    let pollCount = 0
    const maxPolls = 60 // Maximum 5 minutes of polling (60 * 5 seconds)

    if (paymentResponse?.merchantReference) {
      console.log('💳 Starting payment polling for:', paymentResponse.merchantReference)

      intervalId = setInterval(async () => {
        pollCount++
        console.log(`💳 Polling attempt ${pollCount}/${maxPolls} for payment status`)

        try {
          const transaction = await findCallpayTransaction({
            merchantReference: paymentResponse.merchantReference
          })

          console.log('💳 Transaction status check:', transaction?.data)

          // Check for CallPay's actual response format
          if (transaction?.data) {
            const data = transaction.data

            // Handle CallPay's actual response format
            if (data.status === 'authorised' && data.gateway_reference) {
              console.log('💳 Payment successful (authorised status), calling callback')
              console.log('💳 Gateway reference:', data.gateway_reference)
              if (callback) {
                callback(data.gateway_reference)
              }
              clearInterval(intervalId)
              return
            }

            // Check for explicit failure
            if (data.status === 'failed' || data.status === 'declined' || data.status === 'cancelled') {
              console.log('💳 Payment failed with status:', data.status)
              message.error(`Payment ${data.status}. ${data.reason || 'Please try again.'}`)
              clearInterval(intervalId)
              return
            }

            // Check for React Native app format (fallback)
            if (data.hasOwnProperty('success')) {
              if (data.success === 'false') {
                console.log('💳 Payment failed (success=false), stopping polling')
                message.error('Payment failed. Please try again.')
                clearInterval(intervalId)
                return
              }

              if (data.success === 'true') {
                console.log('💳 Payment successful (success=true), calling callback')
                if (callback) {
                  callback(data.gateway_reference || 'payment_successful')
                }
                clearInterval(intervalId)
                return
              }
            }

            // Legacy status checking for backward compatibility
            if (data.status?.toLowerCase() === 'complete') {
              const originalTransaction = await findCallpayOriginalTransaction(paymentResponse.merchantReference)
              console.log('💳 Original transaction status:', originalTransaction?.status)

              if (originalTransaction.status === 'authorised' || originalTransaction.status === 'complete') {
                console.log('💳 Payment successful (legacy check), calling callback')
                if (callback) {
                  callback(data.gateway_reference)
                }
                clearInterval(intervalId)
              }
            }
          } else if (
            transaction?.data?.status?.toLowerCase() === 'failed' ||
            transaction?.data?.status?.toLowerCase() === 'declined' ||
            transaction?.data?.status?.toLowerCase() === 'cancelled'
          ) {
            console.log('💳 Payment failed/declined/cancelled, stopping polling')
            message.error(`Payment ${transaction.data.status.toLowerCase()}. Please try again.`)
            clearInterval(intervalId)
          } else if (pollCount >= maxPolls) {
            console.log('💳 Maximum polling attempts reached, stopping')
            message.error('Payment verification timeout. Please check your payment status or try again.')
            clearInterval(intervalId)
          }
        } catch (error) {
          console.error('💳 Polling error:', error)
          if (pollCount >= maxPolls) {
            message.error('Payment verification failed. Please try again.')
            clearInterval(intervalId)
          }
        }
      }, 5000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [paymentResponse, isAuthorization, callback])

  const handleInitCheckout = async () => {
    try {
      console.log('💳 Initializing CallPay payment:', {
        patientRecord: patientRecord?._id,
        amount,
        paymentType,
        isAuthorization
      })

      const response = await initCallpayPayment(patientRecord, amount, paymentType, isAuthorization)

      console.log('💳 CallPay initialization successful:', {
        merchantReference: response.merchantReference,
        paymentKey: response.paymentKey,
        url: response.url
      })

      setPaymentResponse(response)
    } catch (e) {
      console.error('💳 CallPay initialization failed:', {
        error: e.message,
        response: e.response?.data,
        status: e.response?.status,
        config: {
          url: e.config?.url,
          method: e.config?.method
        }
      })

      const errorMessage = e.response?.data?.message || e.message || 'Payment initialization failed'
      message.error(`Payment setup failed: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {!loading && paymentResponse?.url ? (
        <>
          <iframe
            src={paymentResponse.url}
            title='Callpay Checkout'
            style={{
              width: '100%',
              height: '100vh',
              display: iframeLoading ? 'none' : 'block'
            }}
            onLoad={(e) => {
              setIframeLoading(false)

              // Try to detect when the iframe navigates to payment-status
              try {
                const iframeDoc = e.target.contentDocument || e.target.contentWindow.document
                const currentUrl = iframeDoc.URL || iframeDoc.location.href
                console.log('💳 Iframe loaded URL:', currentUrl)

                if (currentUrl && currentUrl.includes('payment-status')) {
                  console.log('💳 Payment completed - iframe navigated to payment-status')
                  // Payment completed, stop polling and trigger success
                  if (callback) {
                    callback('payment_completed_via_iframe')
                  }
                }
              } catch (error) {
                // Cross-origin restrictions prevent access to iframe content
                console.log('💳 Cannot access iframe content due to cross-origin restrictions')
              }
            }}
          />
          {iframeLoading && (
            <MobMedicalHistoryLoadingOverlay
              loading={true}
              saveLoading={true}
              text='Loading Payment Form...'
              loadingText='Loading Payment Form...'
              icon={<FontAwesomeIcon icon={faCreditCard} />}
            />
          )}
        </>
      ) : (
        <MobMedicalHistoryLoadingOverlay
          loading={true}
          saveLoading={true}
          text='Initializing Payment...'
          loadingText='Initializing Payment...'
          icon={<FontAwesomeIcon icon={faCreditCard} />}
        />
      )}
    </>
  )
}

export default EmbeddedPayment
